import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { EScimUrl } from 'data/urls/scim.url';
import {
  TScimConfigurationDB,
  TScimConfigurationDeterminePositionBasedOnGroupUI,
  TScimConfigurationEnabledUI,
  TScimConfigurationUI,
  TScimGroupIncludeFuturePropertiesUI,
  TScimGroupPositionUI,
  TScimGroupPropertyMappingsUI,
  TScimGroupsPageDB,
  TScimGroupsPageUI,
} from 'data/types/scim.types';
import { TPageableDataParamUI } from 'data/types/pagableData.type';
import { queryParamConverter } from 'data/converters/general.converters';
import {
  scimConfigurationConverter,
  scimConfigurationDeterminePositionBasedOnGroupConverter,
  scimConfigurationEnabledConverter,
  scimGroupIncludeFuturePropertiesConverter,
  scimGroupPositionConverter,
  scimGroupPropertyMappingsConverter,
  scimGroupsPageConverter,
} from 'data/converters/scim.converters';
import { authSplitApi } from '../helpers/slice.helpers';

const { PROPERTY_MANAGER } = EPropertyMangerUrl;

const CONFIGURATION_URL = `${PROPERTY_MANAGER}/${EScimUrl.SCIM}/${EScimUrl.CONFIGURATION}`;

export const scimApi = authSplitApi('scimApi', ['ScimConfiguration', 'ScimGroups']).injectEndpoints({
  endpoints: (build) => ({
    getScimConfiguration: build.query<TScimConfigurationUI | null, string>({
      query(propertyManagementId) {
        return {
          url: CONFIGURATION_URL,
          method: 'GET',
          params: { propertyManagementId },
        };
      },
      transformResponse: (data: TScimConfigurationDB | null) => (data ? scimConfigurationConverter.fromDb(data) : null),
      providesTags: ['ScimConfiguration'],
    }),
    getScimGroups: build.query<TScimGroupsPageUI, TPageableDataParamUI & { propertyManagementId: string }>({
      query({ search, pagination, propertyManagementId }) {
        return {
          url: `${CONFIGURATION_URL}/groups`,
          method: 'GET',
          params: queryParamConverter(search, pagination, { propertyManagementId }, true),
        };
      },
      transformResponse: (data: TScimGroupsPageDB | null) => scimGroupsPageConverter.fromDb(data),
      providesTags: ['ScimGroups'],
    }),
    createScimConfiguration: build.mutation<TScimConfigurationUI, string>({
      query(propertyManagementId) {
        return {
          url: CONFIGURATION_URL,
          method: 'POST',
          body: { propertyManagementId },
        };
      },
      transformResponse: (data: TScimConfigurationDB) => scimConfigurationConverter.fromDb(data),
      invalidatesTags: ['ScimConfiguration', 'ScimGroups'],
    }),
    updateScimConfigurationEnabled: build.mutation<void, TScimConfigurationEnabledUI>({
      query(data) {
        return {
          url: `${CONFIGURATION_URL}/enabled`,
          method: 'PATCH',
          body: scimConfigurationEnabledConverter.toDb(data),
        };
      },
      invalidatesTags: ['ScimConfiguration'],
    }),
    regenerateScimConfigurationToken: build.mutation<TScimConfigurationUI, string>({
      query(propertyManagementId) {
        return {
          url: `${CONFIGURATION_URL}/token/regenerate`,
          method: 'PATCH',
          body: { propertyManagementId },
        };
      },
      transformResponse: (data: TScimConfigurationDB) => scimConfigurationConverter.fromDb(data),
      invalidatesTags: ['ScimConfiguration'],
    }),
    deleteScimConfiguration: build.mutation<void, string>({
      query(propertyManagementId) {
        return {
          url: CONFIGURATION_URL,
          method: 'DELETE',
          body: { propertyManagementId },
        };
      },
      invalidatesTags: ['ScimConfiguration', 'ScimGroups'],
    }),
    setScimGroupPropertyMappings: build.mutation<void, TScimGroupPropertyMappingsUI>({
      query(data) {
        return {
          url: `${CONFIGURATION_URL}/group/property-mappings`,
          method: 'PUT',
          body: scimGroupPropertyMappingsConverter.toDb(data),
        };
      },
      invalidatesTags: ['ScimGroups'],
    }),
    updateScimGroupIncludeFutureProperties: build.mutation<void, TScimGroupIncludeFuturePropertiesUI>({
      query(data) {
        return {
          url: `${CONFIGURATION_URL}/group/include-future-properties`,
          method: 'PATCH',
          body: scimGroupIncludeFuturePropertiesConverter.toDb(data),
        };
      },
      invalidatesTags: ['ScimGroups'],
    }),
    determinePositionBasedOnGroup: build.mutation<void, TScimConfigurationDeterminePositionBasedOnGroupUI>({
      query(data) {
        return {
          url: `${CONFIGURATION_URL}/determine-position-based-on-group`,
          method: 'PATCH',
          body: scimConfigurationDeterminePositionBasedOnGroupConverter.toDb(data),
        };
      },
      invalidatesTags: ['ScimConfiguration'],
    }),
    updateScimGroupPosition: build.mutation<void, TScimGroupPositionUI>({
      query(data) {
        return {
          url: `${CONFIGURATION_URL}/group/position`,
          method: 'PATCH',
          body: scimGroupPositionConverter.toDb(data),
        };
      },
      invalidatesTags: ['ScimGroups'],
    }),
  }),
});

export const {
  useGetScimConfigurationQuery,
  useGetScimGroupsQuery,
  useCreateScimConfigurationMutation,
  useUpdateScimConfigurationEnabledMutation,
  useRegenerateScimConfigurationTokenMutation,
  useDeleteScimConfigurationMutation,
  useSetScimGroupPropertyMappingsMutation,
  useUpdateScimGroupIncludeFuturePropertiesMutation,
  useDeterminePositionBasedOnGroupMutation,
  useUpdateScimGroupPositionMutation,
} = scimApi;
